/**
 * SubscriptionBanner
 *
 * Thin alert strip that appears at the top of the dashboard when subscription state
 * needs user attention. Three states, zero overlap:
 *
 *   past_due                              → yellow "Update payment method"
 *   cancel_at_period_end && active        → orange "Subscription ending on {date}"
 *   canceled && current_period_end future → gray "Subscription ends {date}, reactivate?"
 *
 * All three CTAs route through the Stripe Customer Portal. Hidden for healthy
 * active subscriptions. Returns null when nothing to show — safe to always render
 * as part of layout.
 */

import { useState } from 'react';
import { useSubscription } from '../contexts/SubscriptionContext';
import { API_BASE_URL, fetchWithCsrf } from '../config/api';

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return '';
  }
};

const SubscriptionBanner = () => {
  const { subscription } = useSubscription();
  const [portalLoading, setPortalLoading] = useState(false);
  const [error, setError] = useState(null);

  if (!subscription) return null;

  const openPortal = async () => {
    setPortalLoading(true);
    setError(null);
    try {
      const res = await fetchWithCsrf(`${API_BASE_URL}/payment/portal`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.message || 'Unable to open billing portal');
      }
      window.location.href = data.url;
    } catch (err) {
      console.error('[SubscriptionBanner] Portal error:', err);
      setError(err.message);
      setPortalLoading(false);
    }
  };

  const { status, cancelAtPeriodEnd, currentPeriodEnd } = subscription;
  const periodEndText = formatDate(currentPeriodEnd);

  // State 1: past_due — yellow, most urgent
  if (status === 'past_due') {
    return (
      <BannerShell
        bg="bg-yellow-50"
        border="border-yellow-200"
        textColor="text-yellow-800"
        buttonClass="bg-yellow-600 hover:bg-yellow-700"
        message="Your payment didn't go through. Update your payment method to keep your access."
        buttonText={portalLoading ? 'Opening...' : 'Update Payment'}
        onClick={openPortal}
        disabled={portalLoading}
        error={error}
      />
    );
  }

  // State 2: cancel_at_period_end flipped true and still active — orange warning
  if (cancelAtPeriodEnd && status === 'active') {
    return (
      <BannerShell
        bg="bg-orange-50"
        border="border-orange-200"
        textColor="text-orange-800"
        buttonClass="bg-orange-600 hover:bg-orange-700"
        message={`Your subscription is set to cancel on ${periodEndText}.`}
        buttonText={portalLoading ? 'Opening...' : 'Keep Subscription'}
        onClick={openPortal}
        disabled={portalLoading}
        error={error}
      />
    );
  }

  // State 3: canceled but still in grace period (current_period_end is in the future)
  if (status === 'canceled' && currentPeriodEnd && new Date(currentPeriodEnd) > new Date()) {
    return (
      <BannerShell
        bg="bg-gray-100"
        border="border-gray-300"
        textColor="text-gray-800"
        buttonClass="bg-gray-700 hover:bg-gray-800"
        message={`Your subscription ended. Access remains until ${periodEndText}. Reactivate to keep access.`}
        buttonText={portalLoading ? 'Opening...' : 'Reactivate'}
        onClick={openPortal}
        disabled={portalLoading}
        error={error}
      />
    );
  }

  // Healthy active subscription — no banner
  return null;
};

const BannerShell = ({ bg, border, textColor, buttonClass, message, buttonText, onClick, disabled, error }) => (
  <div className={`${bg} ${border} border-b`}>
    <div className="px-4 py-3 flex items-center justify-between gap-4 flex-wrap">
      <span className={`${textColor} text-sm`}>{message}</span>
      <div className="flex items-center gap-3">
        {error && <span className="text-xs text-red-600">{error}</span>}
        <button
          type="button"
          onClick={onClick}
          disabled={disabled}
          className={`${buttonClass} text-white px-4 py-1.5 rounded text-sm font-medium transition-colors disabled:opacity-50`}
        >
          {buttonText}
        </button>
      </div>
    </div>
  </div>
);

export default SubscriptionBanner;
